import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { sendEmailVerification, onAuthStateChanged } from 'firebase/auth';
import { updateUser } from '../services/userService';
import { Card, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';

const EmailVerification = () => {
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { email } = location.state || {};

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setCheckingStatus(false);
      
      if (!currentUser) {
        // User not logged in, redirect to login
        navigate('/login');
        return;
      }

      setUser(currentUser);

      // If user is already verified, redirect to home
      if (currentUser.emailVerified) {
        navigate('/');
        return;
      }

      // If we have email from signup state, mark that email was sent
      if (email && currentUser.email === email) {
        setEmailSent(true);
      }
    });

    return () => unsubscribe();
  }, [navigate, email]);

  const handleResendVerification = async () => {
    if (!user) return;

    setResendLoading(true);
    setError('');
    setSuccess('');

    try {
      await sendEmailVerification(user);
      setSuccess('Verification email sent! Please check your inbox.');
      setEmailSent(true);
    } catch (error) {
      console.error('Error sending verification email:', error);
      setError('Failed to send verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      // Reload user to get latest email verification status
      await user.reload();
      const updatedUser = auth.currentUser;

      if (updatedUser.emailVerified) {
        // Update user document in Firestore
        await updateUser(updatedUser.uid, { emailVerified: true });
        
        setSuccess('Email verified successfully! Welcome to PulseSync!');
        
        // Redirect to home after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError('Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      setError('Failed to check verification status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (checkingStatus) {
    return (
      <div className="container mt-5">
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card>
              <Card.Body className="text-center">
                <Spinner animation="border" className="mb-3" />
                <p>Checking verification status...</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card>
            <Card.Header className="text-center">
              <h3>Verify Your Email</h3>
              <p className="text-muted mb-0">Almost there!</p>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              {success && (
                <Alert variant="success" className="mb-3">
                  {success}
                </Alert>
              )}

              <div className="text-center mb-4">
                <div className="mb-3">
                  <i className="fas fa-envelope fa-3x text-primary"></i>
                </div>
                
                <h5>Check Your Email</h5>
                <p className="text-muted">
                  We've sent a verification link to:
                </p>
                <p className="fw-bold">{user?.email || email}</p>
                
                {emailSent && (
                  <Alert variant="info" className="mt-3">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Email sent!</strong> Please check your inbox and spam folder.
                  </Alert>
                )}
              </div>

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={handleCheckVerification}
                  disabled={loading}
                  className="mb-2"
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Checking...
                    </>
                  ) : (
                    'I\'ve Verified My Email'
                  )}
                </Button>

                <Button
                  variant="outline-secondary"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="mb-2"
                >
                  {resendLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>

                <hr className="my-3" />

                <div className="text-center">
                  <p className="text-muted small mb-2">
                    Need help? Check your spam folder or contact support.
                  </p>
                  <Button
                    variant="link"
                    onClick={handleSignOut}
                    className="text-decoration-none"
                  >
                    Sign Out
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EmailVerification;
